import  { type OnDemandContent, type OnDemandItem, ContentType, type PlayItem, type ContentProperties, ContentItemProps} from "@ca.vmedia.tv/domains";
import { Services } from "@ca.vmedia.tv/services";
import { useDetailPageStore } from "@/stores/DetailPage";
import { SeasonsCardData } from "@/components/Cards/types";
import { Logger } from '@/utils';
import { AppConstants } from "@/common";
import { useEmptyLine } from "@/composables/useEmptyLine";
import type { ExposeInterface } from './types';
import type { AxiosError } from 'axios';
import { useUserInfoStore } from "@/stores/UserInfo";

export const useDetailShowPageStore = defineStore('DetailShowPage', (): ExposeInterface => {
    const UserInfoStoreModule = useUserInfoStore();
    const {
        isClient,
    } = storeToRefs(UserInfoStoreModule);

    const { 
        emptyOnDemandContent
    } = useEmptyLine();

    const DetailPageStore = useDetailPageStore();
    const {
        getCurrentItemDetail,
    } = storeToRefs(DetailPageStore);


    const _seasons: Ref<OnDemandContent | null> = ref(null);
    const getSeasons: ComputedRef<SeasonsCardData[] | null> = computed(() => {
        const data = _seasons.value;

        if (data === null || data.items === null) {
            return null;
        }

        if (data.items.length === 0) {
            return [];
        }

        return data?.items?.map((i: OnDemandItem, index: number) => new SeasonsCardData(i, index)) ?? []
    });
    const fetchSeasons = async (): Promise<void> => {
        try {
            if (!getCurrentItemDetail.value?.id) {
                return;
            }

            const isVOD = (getCurrentItemDetail.value.contentType === ContentType.VOD || getCurrentItemDetail.value.contentType === ContentType.SVOD);

            _seasons.value = isVOD
                ? await Services.Instance.vod.getSVODSeasons(getCurrentItemDetail.value.id, 999, 1)
                : await Services.Instance.vod.getCraveTVSeasons(getCurrentItemDetail.value.id, 999, 1);

            if (_seasons.value.items && _seasons.value.items.length > 0){
                const items = _seasons.value?.items?.map((i: OnDemandItem, index: number) => new SeasonsCardData(i, index)) ?? [];
                setCurrentSeason(items[0]);
            } else {
                setCurrentSeason(null);
            }
        } catch (error) {
            _seasons.value = emptyOnDemandContent();
            Logger.captureError(error as AxiosError | Error);
        }
    };

    const _currentSeason: Ref<SeasonsCardData | null> = ref(null);
    const setCurrentSeason = (season: SeasonsCardData | null): void => {
        if (getSeasons.value === null) {
            return;
        }
        
        if(season === null) {
            _currentSeason.value = getSeasons.value[0];
            return;
        }
        
        _currentSeason.value = season;
    };
    const getCurrentSeason: ComputedRef<SeasonsCardData | null> = computed(() => _currentSeason.value);

    const _isLoadingEpisodes: Ref<boolean> = ref(false);
    const getIsLoadingEpisodes: ComputedRef<boolean> = computed(() => _isLoadingEpisodes.value);
    const _setIsLoadingEpisodes = (isLoading: boolean) => {
        _isLoadingEpisodes.value = isLoading;
    };

    const _episodesProperties: Ref<ContentProperties[]> = ref([]);
    const getEpisodesProperties: ComputedRef<ContentProperties[]> = computed(() => _episodesProperties.value);

    const _episodes: Ref<OnDemandContent | null> = ref(null);
    const getEpisodes: ComputedRef<OnDemandContent | null> = computed(() => _episodes.value);
    const _fetchEpisodes = async (item: PlayItem | null) => {
        try {
            if (item === null || !item.id) {
                _episodes.value = emptyOnDemandContent();
                return;
            }

            _clearEpisodes();
            const isVOD = [ContentType.VOD, ContentType.SVOD].includes(item.contentType as ContentType);
            _episodes.value = isVOD
                ? await Services.Instance.vod.getSVODSeasonEpisodes(item.id, AppConstants.EpisodesInLine, 1)
                : await Services.Instance.vod.getCraveTVSeasonEpisodes(item.id, AppConstants.EpisodesInLine, 1);
        } catch (error) {
            _episodes.value = emptyOnDemandContent();
            Logger.captureError(error as AxiosError | Error);
        }
    };
    const fetchNextPageOfEpisodes = async (): Promise<void> => {
        if (!getEpisodes.value || !getEpisodes.value.items || !getEpisodes.value.paging?.itemsOnPage) {
            return;
        }
        const pageNumber = Math.ceil(getEpisodes.value.items.length / getEpisodes.value.paging.itemsOnPage) + 1;
        if (pageNumber > (getEpisodes.value.paging?.totalPages ?? 0)) {
            return;
        }
        
        try {
            _setIsLoadingEpisodes(true);
            const item = _currentSeason.value?.getItem();

            const isVOD = [ContentType.VOD, ContentType.SVOD].includes(item?.contentType as ContentType);

            if (!item?.id) {
                throw new Error('Item id is not defined');
            }

            const data = isVOD
                ? await Services.Instance.vod.getSVODSeasonEpisodes(item.id, AppConstants.EpisodesInLine, pageNumber)
                : await Services.Instance.vod.getCraveTVSeasonEpisodes(item.id, AppConstants.EpisodesInLine, pageNumber);

            if (!data?.items || !_episodes?.value?.items) {
                throw new Error('Items are not defined');
            }
            _episodes.value.items = [..._episodes.value.items, ...data.items];
        } catch (error) {
            Logger.captureError(error as AxiosError | Error);
        } finally {
            _setIsLoadingEpisodes(false);
        }
    };
    const _clearEpisodes = (): void => {
        _episodes.value = null;
        _episodesProperties.value = [];
    };


    const clearAllDetailShowPage = (): void => {
        _currentSeason.value = null;
        _seasons.value = null;
        _clearEpisodes();
    }; 

    watch(
        _currentSeason,
        (value: SeasonsCardData | null) => {
            if (value instanceof SeasonsCardData) {
                _fetchEpisodes(value.getItem());
            } else {
                _fetchEpisodes(null);
            }
        },
    );

    watch(
        () => getEpisodes.value ? getEpisodes.value.items : null,
        (items) => {
            if(isClient.value && items) {
                const ids = items.slice(-AppConstants.EpisodesInLine).map((itemEpisode: OnDemandItem) => itemEpisode.id);
                Services.Instance.domains.mediaRepository.getItemProperties(
                    ids as number[], 
                    getCurrentItemDetail.value?.contentType as ContentType, 
                    [ContentItemProps.WatchedPosition]
                ).then((streamProperties) => {
                    _episodesProperties.value = [..._episodesProperties.value, ...streamProperties];
                }).catch((error) => {
                    Logger.captureError(error as AxiosError | Error);
                });
            }
        }
    );

    return {
        getEpisodes,
        getEpisodesProperties,
        getCurrentSeason,
        getSeasons,
        getIsLoadingEpisodes,
        fetchSeasons,
        setCurrentSeason,
        clearAllDetailShowPage,
        fetchNextPageOfEpisodes,
    };
});
